import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SimpleSelect } from 'react-selectize-advizr';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import AdvForm from '../../../CommonComponents/AdvForm';
import Footer from '../../Footer/FooterContainer';
import Loading from '../../Loading/LoadingView';
import { isNumeric, isUuid, createCustomPropType } from '../../../../js/app/react/validators';
import ValidationErrors from '../../ValidationErrors/ValidationErrorsContainer';
import AdvInputWrapper from '../../../CommonComponents/AdvInputWrapper';
import AdvInput from '../../../CommonComponents/AdvInput';
import {
  simpleSelectDefaultValue,
  simpleSelectChangedToNonNull,
} from '../../../Profile/Accounts_react/AccountsUtils';

export const shouldQueryPlanImpact = (previousProps, props) => {
  const impactTriggerProps = ['whatIf', 'fpId', 'isOPLMonteCarlo'];
  const pickedProps = cloneDeep(pick(props, impactTriggerProps));
  const pickedPreviousProps = cloneDeep(pick(previousProps, impactTriggerProps));

  const requiredProps = ['yearsAmount', 'principalAge'];
  if (requiredProps.some(propKey => !isNumeric(props[propKey]))) {
    return false;
  }

  if (props.yearsAmount === 0 || props.principalAge === 0) {
    return false;
  }

  return !isEqual(pickedPreviousProps, pickedProps);
};

class MarketDropsView extends Component {
  componentDidMount() {
    const {
      fetchData,
      clientId,
      fpId,
      goalId,
      whatIfTemplate,
      fetchInitialPlanImpact,
      whatIf,
    } = this.props;
    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);

    if (shouldQueryPlanImpact({}, this.props)) {
      fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  componentDidUpdate(previousProps) {
    const { clientId, fpId, goalId, whatIf, whatIfTemplate, fetchPlanImpact } = this.props;

    if (shouldQueryPlanImpact(previousProps, this.props)) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }

  render() {
    const {
      goalId,
      clientId,
      loading,
      groupTitle,
      storeData,
      fpId,
      whatIf,
      whatIfTemplate,
      applyWhatIf,
      areOptionsLoaded,
      marketDropsPercentageChanged,
      principalName,
      marketDropsPercentage,
      principalAgeOptions,
      principalAge,
      yearsAmountOptions,
      yearsAmount,
    } = this.props;
    const { ADV } = window;
    return (
      <div className="what-ifs-layout">
        {loading && <Loading />}
        <div className="what-ifs-section what-ifs-layout-header">
          <h3>What-If...</h3>
        </div>
        <div className="what-ifs-layout-content">
          <h4 className="what-if-group-title">{groupTitle}</h4>
          <p className="what-if-stress-test-title-description">
            Use this section to see the effect of changes in market conditions.
          </p>
          <AdvForm>
            <ValidationErrors clientId={clientId} />
            <ul className={`what-ifs-form-fields what-ifs-box${areOptionsLoaded ? ' loaded' : ''}`}>
              <li className="what-ifs-form-field">
                <div className="what-ifs-market-drops-layout-1">
                  <h5 className="what-if-title what-ifs-market-drops-layout-text">
                    The market drops?
                  </h5>
                  <AdvInputWrapper
                    hasPercentage
                    className="what-ifs-input-wrapper what-ifs-market-drops-layout-input"
                  >
                    <AdvInput
                      type="text"
                      dataType="interest"
                      maxLength="14"
                      onBlur={marketDropsPercentageChanged(
                        clientId,
                        goalId,
                        whatIfTemplate.WhatIfType,
                      )}
                      nullable
                      value={ADV.util.formatValue(marketDropsPercentage, 'interest')}
                      className="what-ifs-input what-ifs-market-drops-percentage-input"
                    />
                  </AdvInputWrapper>
                </div>
              </li>
              <li className="what-ifs-form-field">
                <div className="what-ifs-market-drops-layout-1">
                  <h5 className="what-if-title what-ifs-market-drops-layout-text">
                    When {principalName} is
                  </h5>
                  <div className="what-ifs-market-drops-layout-input">
                    <SimpleSelect
                      id="MarketDropsStartingAge"
                      className="what-ifs-simple-select what-ifs-market-drops-select"
                      value={simpleSelectDefaultValue(principalAgeOptions, principalAge)}
                      onValueChange={simpleSelectChangedToNonNull(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.MarketDropsStartingAge',
                      )}
                      options={principalAgeOptions}
                    />
                  </div>
                </div>
              </li>
              <li className="what-ifs-form-field">
                <div className="what-ifs-market-drops-layout-2">
                  <h5 className="what-if-title what-ifs-market-drops-layout-for-text">for</h5>
                  <div className="what-ifs-market-drops-layout-for-input">
                    <SimpleSelect
                      id="year"
                      className="what-ifs-simple-select what-ifs-market-drops-select"
                      value={simpleSelectDefaultValue(yearsAmountOptions, yearsAmount)}
                      onValueChange={simpleSelectChangedToNonNull(
                        clientId,
                        storeData(goalId, whatIfTemplate.WhatIfType),
                        'WhatIf.MarketDropsNumYears',
                      )}
                      options={yearsAmountOptions}
                    />
                  </div>
                  <h5 className="what-if-title what-ifs-market-drops-layout-years-text">year(s)</h5>
                </div>
              </li>
            </ul>
          </AdvForm>
        </div>
        <Footer
          clientId={clientId}
          applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
        />
      </div>
    );
  }
}
MarketDropsView.defaultProps = {
  groupTitle: 'Untitled What If Group',
  principalAge: null,
  yearsAmount: null,
};

MarketDropsView.propTypes = {
  goalId: createCustomPropType(isUuid).isRequired,
  fpId: createCustomPropType(isUuid).isRequired,
  clientId: createCustomPropType(isNumeric).isRequired,
  marketDropsPercentage: PropTypes.number.isRequired,
  groupTitle: PropTypes.string,
  areOptionsLoaded: PropTypes.bool.isRequired,
  whatIf: PropTypes.shape({}).isRequired,
  whatIfTemplate: PropTypes.shape({}).isRequired,
  fetchData: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  principalName: PropTypes.string.isRequired,
  principalAgeOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  principalAge: PropTypes.number,
  yearsAmountOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  yearsAmount: PropTypes.number,
  applyWhatIf: PropTypes.func.isRequired,
  marketDropsPercentageChanged: PropTypes.func.isRequired,
  storeData: PropTypes.func.isRequired,
  fetchInitialPlanImpact: PropTypes.func.isRequired,
  fetchPlanImpact: PropTypes.func.isRequired,
};

export default MarketDropsView;
